/**
 * Vue « consultation » d'un bloc de leçon V2. Affiche : icône type + contenu
 * markdown (KaTeX) + actions (ordre, éditer, dupliquer, supprimer) + pills des
 * liens vers des steps d'exercices (corrigé / distracteur).
 */

import { useState } from 'react';
import {
  BookOpen,
  Heading as HeadingIcon,
  Lightbulb,
  Link2,
  StickyNote,
  Text as TextIcon,
  type LucideIcon,
} from 'lucide-react';
import { cn, Button, MarkdownMath } from '@jeraisonne/ui';
import type { LessonBlock, LessonBlockType } from '@/lib/lesson-v2/types';
import type { LessonBlockLink } from '@/lib/lesson-v2/types';

const TYPE_META: Record<
  LessonBlockType,
  { label: string; Icon: LucideIcon; color: string; border: string }
> = {
  heading: {
    label: 'Titre',
    Icon: HeadingIcon,
    color: 'text-blue-700 bg-blue-50 ring-blue-200',
    border: 'border-blue-200',
  },
  text: {
    label: 'Texte',
    Icon: TextIcon,
    color: 'text-slate-700 bg-slate-50 ring-slate-200',
    border: 'border-slate-200',
  },
  key_point: {
    label: 'Point clé',
    Icon: Lightbulb,
    color: 'text-amber-700 bg-amber-50 ring-amber-200',
    border: 'border-amber-200',
  },
  example: {
    label: 'Exemple',
    Icon: BookOpen,
    color: 'text-emerald-700 bg-emerald-50 ring-emerald-200',
    border: 'border-emerald-200',
  },
  note: {
    label: 'Note tuteur',
    Icon: StickyNote,
    color: 'text-slate-600 bg-slate-100 ring-slate-300',
    border: 'border-slate-300',
  },
};

const LINK_LABEL: Record<string, string> = {
  teaches: 'enseigne',
  misconception: 'corrige',
  reinforces: 'renforce',
};

function buildBlockMarkdown(block: LessonBlock): string {
  const d = block.data as Record<string, string | number | undefined>;
  switch (block.block_type) {
    case 'heading': {
      const level = typeof d.level === 'number' ? Math.min(Math.max(d.level, 1), 3) : 2;
      return `${'#'.repeat(level)} ${d.text ?? ''}`;
    }
    case 'text':
      return String(d.content_md ?? '');
    case 'key_point': {
      const label = d.label ? `**${d.label}**\n\n` : '';
      return label + String(d.content_md ?? '');
    }
    case 'example': {
      const title = d.title ? `**${d.title}**\n\n` : '';
      return title + String(d.content_md ?? '');
    }
    case 'note':
      return String(d.body_md ?? '');
  }
}

export interface LessonBlockCardProps {
  block: LessonBlock;
  links: LessonBlockLink[];
  isFirst: boolean;
  isLast: boolean;
  isDisabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onAddLink?: () => void;
  onRemoveLink?: (linkId: string) => void;
}

export function LessonBlockCard(props: LessonBlockCardProps) {
  const {
    block,
    links,
    isFirst,
    isLast,
    isDisabled,
    onMoveUp,
    onMoveDown,
    onEdit,
    onDuplicate,
    onDelete,
    onAddLink,
    onRemoveLink,
  } = props;
  const meta = TYPE_META[block.block_type];
  const Icon = meta.Icon;
  const markdown = buildBlockMarkdown(block);
  const hasContent = markdown.trim().length > 0;
  const [showActions, setShowActions] = useState(false);
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <article
      data-testid={`lesson-block-${block.id}`}
      data-mode="view"
      onClick={() => setShowActions((v) => !v)}
      className={cn(
        'group relative flex cursor-pointer overflow-hidden rounded-lg border bg-white shadow-sm transition-all hover:border-slate-400',
        meta.border,
        showActions && 'ring-1 ring-slate-300',
      )}
    >
      <div
        className={cn('flex w-10 shrink-0 items-start justify-center py-3 sm:w-12', meta.color)}
        title={meta.label}
        aria-label={meta.label}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </div>

      <div className="flex min-w-0 flex-1 flex-col p-3">
        {hasContent ? (
          <MarkdownMath className="text-sm text-slate-800">{markdown}</MarkdownMath>
        ) : (
          <p className="text-sm italic text-slate-400">(vide)</p>
        )}

        {/* Pills des liens vers des steps d'exercices */}
        {links.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1" onClick={stop}>
            {links.map((l) => (
              <span
                key={l.id}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium',
                  l.exercise_block_type === 'distractor'
                    ? 'border-amber-300 bg-amber-50 text-amber-800'
                    : 'border-emerald-300 bg-emerald-50 text-emerald-800',
                )}
                title={`${l.exercise_title ?? 'Exercice'} — ${l.exercise_block_preview ?? ''}`}
              >
                <Link2 className="h-3 w-3" aria-hidden />
                {LINK_LABEL[l.link_type] ?? l.link_type} ·{' '}
                {(l.exercise_block_preview || l.exercise_title || '…').slice(0, 32)}
                {onRemoveLink ? (
                  <button
                    type="button"
                    onClick={() => onRemoveLink(l.id)}
                    disabled={isDisabled}
                    className="ml-0.5 rounded-full px-1 text-current hover:bg-black/10"
                    aria-label="Retirer le lien"
                  >
                    ✕
                  </button>
                ) : null}
              </span>
            ))}
          </div>
        ) : null}

        {showActions ? (
          <div
            className="mt-3 flex flex-wrap items-center gap-1 border-t border-slate-100 pt-2"
            onClick={stop}
          >
            {onAddLink && block.section === 'content' ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onAddLink}
                disabled={isDisabled}
              >
                <Link2 className="mr-1 h-3.5 w-3.5" /> Lier à un exercice
              </Button>
            ) : null}

            <div className="ml-auto flex items-center gap-0.5">
              <button
                type="button"
                onClick={onMoveUp}
                disabled={isFirst || isDisabled}
                className="min-h-9 min-w-9 rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                aria-label="Monter"
                title="Monter"
              >
                ▲
              </button>
              <button
                type="button"
                onClick={onMoveDown}
                disabled={isLast || isDisabled}
                className="min-h-9 min-w-9 rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                aria-label="Descendre"
                title="Descendre"
              >
                ▼
              </button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onEdit}
                disabled={isDisabled}
              >
                Éditer
              </Button>
              <button
                type="button"
                onClick={onDuplicate}
                disabled={isDisabled}
                className="min-h-9 min-w-9 rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                aria-label="Dupliquer"
                title="Dupliquer"
              >
                ⎘
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={isDisabled}
                className="min-h-9 min-w-9 rounded p-1 text-red-500 hover:bg-red-50 disabled:opacity-30"
                aria-label="Supprimer"
                title="Supprimer"
              >
                ✕
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
}
